import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { Candidate } from "../../types";
import apiService from "../../services/api";
import ContactModal from "../../components/ContactModal";
import Tooltip from "../../components/Tooltip";
import Toast from "../../components/Toast";
import VerifiedBadge from "../../components/VerifiedBadge";
import { format } from "date-fns";
import { getJobApplicationViewDetails } from "./JobManagement/api/job_api";
import { useCandidateViewModel } from "./Candidate/model/CandidateViewModel";
import { getTalentMarketPlaceSendMessage } from "./Candidate/api/Candidate.api";
import { formatAvailability } from "../../utils/displayFormatters";

const CandidateProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { handleTalentMarketPlaceViewContact } = useCandidateViewModel();
  const location = useLocation();
  const source = location.state?.source || "company";
  const [candidate, setCandidate] = useState<Candidate | null>(
    source === "marketplace" ? location.state?.candidate ?? null : null,
  );
  const [loading, setLoading] = useState(source === "company");
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    | "overview"
    | "experience"
    | "education"
    | "skills"
    | "projects"
    | "certifications"
    | "additional"
  >("overview");

  // Contact modal state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning" | "info";
    isVisible: boolean;
  }>({
    message: "",
    type: "success",
    isVisible: false,
  });
  const [contactUnlockError, setContactUnlockError] = useState<string | null>(
    null,
  );
  useEffect(() => {
    if (source === "marketplace") {
      // Use data already in navigation state — no API call
      const stateCandidate = location.state?.candidate;
      if (stateCandidate) {
        setCandidate(stateCandidate);
      } else {
        setError("Candidate data not found.");
      }
    } else if (id) {
      loadCandidate(id);
    }
  }, [id, source]);

  const loadCandidate = async (candidateId: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getCandidate(candidateId);
      setCandidate(data);
    } catch (err: any) {
      if (
        err.message?.includes("credentials") ||
        err.message?.includes("401") ||
        err.message?.includes("Unauthorized")
      ) {
        setError(
          "Authentication expired. Please refresh the page or log in again.",
        );
      } else if (
        err.message?.includes("404") ||
        err.message?.includes("not found")
      ) {
        setError(
          "Candidate not found. They may have been removed or you may not have access.",
        );
      } else {
        setError(err.message || "Failed to load candidate");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContact = () => {
    setContactModalOpen(true);
  };
  const handleSendMessage = async (message: string, subject: string) => {
    if (!candidate) return;
    if (!candidate.id) {
      setToast({
        message: "Cannot send message: candidate ID is missing.",
        type: "error",
        isVisible: true,
      });
      return;
    }
    try {
      if (source === "marketplace") {
        await getTalentMarketPlaceSendMessage(candidate.id, {
          subject,
          message,
          access_type: "marketplace",
        });
      } else {
        await apiService.contactCandidate(candidate.id, {
          subject,
          message,
        });
      }
      setToast({
        message: `🎉 Message sent successfully to ${candidate.first_name} ${candidate.last_name}!`,
        type: "success",
        isVisible: true,
      });
      setContactModalOpen(false);
    } catch (err: any) {
      console.error("Failed to send message:", err);
      const apiMessage = err?.response?.data?.message || err?.message;
      setToast({
        message: apiMessage || "Failed to send message. Please try again.",
        type: "error",
        isVisible: true,
      });
    }
  };
  // const handleViewContact = async (
  //   //candidateId: string,
  //   applicationId: string,
  // ) => {
  //   try {
  //     // dispatch({ type: "SET_CANDIDATE_VIEW_LOADING", payload: true });
  //     if (!applicationId) {
  //       console.error("Application ID missing");
  //       return;
  //     }
  //     const payload = {
  //       access_type: "applicant",
  //       application_id: applicationId,
  //     };
  //     console.log("candidateId", payload);
  //     // await viewContactDetails(candidateId, payload);
  //     const resp = await getJobApplicationViewDetails(applicationId);
  //     // if (state.applicationQuery) {
  //     //   await fetchJobApplications(state.applicationQuery);
  //     // }
  //     // dispatch({
  //     //   type: "SET_VIEW_APPLICATIONS",
  //     //   payload: resp?.data ?? null,
  //     // });
  //   } catch (error) {
  //     console.error("View contact failed", error);
  //   } finally {
  //     //  dispatch({ type: "SET_CANDIDATE_VIEW_LOADING", payload: false });
  //   }
  // };
  // const handleContactCandidate = async (message: string, subject: string) => {
  //   if (!candidate) return;

  //   try {
  //     await apiService.contactCandidate(candidate.id, {
  //       subject,
  //       message,
  //     });

  //     setToast({
  //       message: `🎉 Message sent successfully to ${candidate.first_name} ${candidate.last_name}!`,
  //       type: "success",
  //       isVisible: true,
  //     });

  //     setContactModalOpen(false);
  //   } catch (error) {
  //     console.error("Failed to send message:", error);
  //     setToast({
  //       message: "Failed to send message. Please try again.",
  //       type: "error",
  //       isVisible: true,
  //     });
  //   }
  // };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  // if (loading) {
  //   return (
  //     <div className="p-6">
  //       <div className="animate-pulse">
  //         <div className="flex items-center mb-6">
  //           <div className="h-4 bg-gray-200 rounded w-32 mr-4"></div>
  //           <div className="h-4 bg-gray-200 rounded w-4"></div>
  //           <div className="h-4 bg-gray-200 rounded w-48 ml-4"></div>
  //         </div>
  //         <div className="bg-white rounded-lg shadow p-6">
  //           <div className="flex items-center mb-6">
  //             <div className="h-20 w-20 bg-gray-200 rounded-full mr-6"></div>
  //             <div>
  //               <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
  //               <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
  //               <div className="h-4 bg-gray-200 rounded w-40"></div>
  //             </div>
  //           </div>
  //           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
  //             <div className="h-32 bg-gray-200 rounded"></div>
  //             <div className="h-32 bg-gray-200 rounded"></div>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !candidate) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          <div className="flex items-start">
            <svg
              className="h-5 w-5 text-red-400 mr-2 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="font-medium">Unable to load candidate profile</p>
              <p className="text-sm mt-1">{error || "Candidate not found"}</p>

              {/* Show refresh button for authentication errors */}
              {error?.includes("Authentication") && (
                <div className="mt-3 flex space-x-3">
                  <button
                    onClick={() => window.location.reload()}
                    className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm font-medium"
                  >
                    Refresh Page
                  </button>
                  <button
                    onClick={() => (window.location.href = "/auth/login")}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-medium"
                  >
                    Re-login
                  </button>
                </div>
              )}

              {/* Show back button for other errors */}
              {!error?.includes("Authentication") && (
                <div className="mt-3">
                  <button
                    onClick={() => window.history.back()}
                    className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm font-medium"
                  >
                    Go Back
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "overview", name: "Overview", icon: "👤" },
    { id: "experience", name: "Experience", icon: "💼" },
    { id: "education", name: "Education", icon: "🎓" },
    { id: "skills", name: "Skills", icon: "⚡" },
    { id: "projects", name: "Projects", icon: "🚀" },
    { id: "certifications", name: "Certifications", icon: "🏆" },
    { id: "additional", name: "Languages & Awards", icon: "🌟" },
    // { id: "resumes", name: "Resumes", icon: "📄" },
  ];
  const formatMonthYear = (date?: string) => {
    if (!date) return "";
    return format(new Date(date), "MMM yyyy");
  };

  // company API key: "experiences" (plural), marketplace API key: "experience" (singular)
  // field names inside each item are identical in both
  const experiences: any[] =
    (candidate as any).experiences ?? (candidate as any).experience ?? [];
  const isVerified =
    candidate.is_identity_verified === true || candidate.is_id_verified === true;

  const toStringList = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];

    return value.filter(
      (item): item is string => typeof item === "string" && item.trim() !== "",
    );
  };

  const getExperienceAchievements = (experience: any): string[] =>
    toStringList(experience.achievements ?? experience.key_achievements);
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Breadcrumb */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link
              to="/company/candidates"
              state={{ activeTab: source }}
              className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-indigo-600"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                />
              </svg>
              Candidates
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg
                className="w-6 h-6 text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">
                {[candidate.first_name, candidate.last_name]
                  .filter(Boolean)
                  .join(" ") || "No title"}
              </span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0 h-20 w-20">
              <div className="h-20 w-20 rounded-full bg-indigo-100 flex items-center justify-center">
                <span className="text-indigo-600 font-bold text-2xl">
                  {(candidate.first_name?.charAt(0) || "") +
                    (candidate.last_name?.charAt(0) || "") || "NA"}
                </span>
              </div>
            </div>
            <div className="ml-6">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  {candidate.first_name} {candidate.last_name}
                </h1>
                {isVerified && <VerifiedBadge />}
              </div>
              <p className="text-lg text-gray-600 mt-1">
                {candidate.current_job_title}{" "}
                {candidate.current_company && `at ${candidate.current_company}`}
              </p>
              <div className="flex items-center mt-2 space-x-4">
                {candidate.location && (
                  <span className="text-sm text-gray-500 flex items-center">
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {candidate.location}
                  </span>
                )}
                {candidate.years_of_experience && (
                  <span className="text-sm text-gray-500">
                    {candidate.years_of_experience} years experience
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleContact}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              Contact
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="w-full bg-white rounded-lg shadow-sm border flex flex-col min-h-0">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 min-h-0">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="relative ">
              <div>
                <div className="space-y-6">
                  {/* Contact Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Contact Information
                      </h3>
                      <dl className="space-y-3">
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Email
                          </dt>
                          <dd className="text-sm text-gray-900">
                            {candidate.email}
                          </dd>
                        </div>
                        {candidate.phone && (
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Phone
                            </dt>
                            <dd className="text-sm text-gray-900">
                              {candidate.phone}
                            </dd>
                          </div>
                        )}
                        {candidate.linkedin_url && (
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              LinkedIn
                            </dt>
                            <dd className="text-sm text-gray-900">
                              <a
                                href={candidate.linkedin_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 hover:text-indigo-500"
                              >
                                View Profile
                              </a>
                            </dd>
                          </div>
                        )}
                        {candidate.github_url && (
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              GitHub
                            </dt>
                            <dd className="text-sm text-gray-900">
                              <a
                                href={candidate.github_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 hover:text-indigo-500"
                              >
                                View Profile
                              </a>
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>

                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Professional Summary
                      </h3>
                      {candidate.professional_summary ? (
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {candidate.professional_summary}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          No summary available
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Skills */}
                  {/* {candidate.skills && candidate.skills.length > 0 && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Skills
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {candidate.skills.map((skill, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {skill.skill_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {skill.proficiency_level}
                                {skill.years_of_experience &&
                                  ` • ${skill.years_of_experience} years`}
                              </p>
                            </div>
                            {skill.confidence_score && (
                              <div className="text-right">
                                <div className="text-xs text-gray-500">
                                  Confidence
                                </div>
                                <div className="text-sm font-medium text-indigo-600">
                                  {Math.round(skill.confidence_score * 100)}%
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )} */}

                  {/* Industry Sectors */}
                  {/* {candidate.ai_sector_classifications &&
                    candidate.ai_sector_classifications.length > 0 && (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                          Industry Sectors
                        </h3>
                        <div className="space-y-4">
                          {candidate.ai_sector_classifications.map(
                            (sector, index) => (
                              <div
                                key={index}
                                className="p-4 bg-purple-50 rounded-lg border border-purple-200"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center mb-2">
                                      <h4 className="text-sm font-semibold text-purple-900">
                                        {sector.sector_name}
                                      </h4>
                                      {sector.subsector && (
                                        <span className="ml-2 text-sm text-purple-700">
                                          • {sector.subsector}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-sm text-purple-800 mb-2">
                                      {sector.reasoning}
                                    </p>
                                    {sector.keywords_matched &&
                                      sector.keywords_matched.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                          {sector.keywords_matched.map(
                                            (keyword, i) => (
                                              <span
                                                key={i}
                                                className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800"
                                              >
                                                {keyword}
                                              </span>
                                            ),
                                          )}
                                        </div>
                                      )}
                                  </div>
                                  <div className="ml-4 text-right">
                                    <div className="text-xs text-purple-600 mb-1">
                                      Confidence
                                    </div>
                                    <div className="text-lg font-bold text-purple-900">
                                      {Math.round(sector.confidence * 100)}%
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                        {candidate.ai_primary_sector && (
                          <div className="mt-4 p-3 bg-purple-100 rounded-lg">
                            <div className="flex items-center">
                              <svg
                                className="w-5 h-5 text-purple-600 mr-2"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span className="text-sm font-medium text-purple-900">
                                Primary Sector: {candidate.ai_primary_sector}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )} */}

                  {/* Professional Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Work Preferences
                      </h3>
                      <dl className="space-y-3">
                        {candidate.availability && (
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Availability
                            </dt>
                            <dd className="text-sm text-gray-900">
                              {formatAvailability(candidate.availability)}
                            </dd>
                          </div>
                        )}
                        {candidate.preferred_work_type && (
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Work Type Preference
                            </dt>
                            <dd className="text-sm text-gray-900 capitalize">
                              {candidate.preferred_work_type.replace("_", " ")}
                            </dd>
                          </div>
                        )}
                        {candidate.willing_to_relocate !== undefined &&
                          candidate.willing_to_relocate !== null && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Willing to Relocate
                              </dt>
                              <dd className="text-sm text-gray-900">
                                {String(candidate.willing_to_relocate)}
                              </dd>
                            </div>
                          )}
                        {candidate.work_authorization && (
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Work Authorization
                            </dt>
                            <dd className="text-sm text-gray-900">
                              {candidate.work_authorization}
                            </dd>
                          </div>
                        )}
                        {candidate.expected_salary != null && (
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Expected Salary
                            </dt>
                            <dd className="text-sm text-gray-900">
                              {candidate.salary_currency
                                ? `${candidate.salary_currency} ${Number(candidate.expected_salary).toLocaleString()}`
                                : Number(candidate.expected_salary).toLocaleString()}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>

                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Additional Details
                      </h3>
                      <dl className="space-y-3">
                        {candidate.total_years_experience && (
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Total Experience
                            </dt>
                            <dd className="text-sm text-gray-900">
                              {candidate.total_years_experience} years
                            </dd>
                          </div>
                        )}

                        {candidate.source && (
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Source
                            </dt>
                            <dd className="text-sm text-gray-900 capitalize">
                              {candidate.source}
                            </dd>
                          </div>
                        )}
                        {candidate.personal_website && (
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Personal Website
                            </dt>
                            <dd className="text-sm text-gray-900">
                              <a
                                href={candidate.personal_website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 hover:text-indigo-500"
                              >
                                Visit Website
                              </a>
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Languages */}
                    {candidate.languages &&
                      Array.isArray(candidate.languages) &&
                      candidate.languages.length > 0 && (
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Languages
                          </h3>
                          <div className="space-y-2">
                            {candidate.languages.map(
                              (lang: any, index: number) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between p-3 bg-blue-50 rounded-lg"
                                >
                                  <span className="text-sm font-medium text-blue-900">
                                    {typeof lang === "string"
                                      ? lang
                                      : lang.language || lang.name}
                                  </span>
                                  {typeof lang === "object" &&
                                    lang.proficiency && (
                                      <span className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded">
                                        {lang.proficiency}
                                      </span>
                                    )}
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                    {/* Certifications */}
                    {candidate.certifications &&
                      Array.isArray(candidate.certifications) &&
                      candidate.certifications.length > 0 && (
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Certifications
                          </h3>
                          <div className="space-y-3">
                            {candidate.certifications.map(
                              (cert: any, index: number) => (
                                <div
                                  key={index}
                                  className="p-3 bg-green-50 rounded-lg border border-green-200"
                                >
                                  <h4 className="text-sm font-medium text-green-900">
                                    {typeof cert === "string"
                                      ? cert
                                      : cert.name || cert.certification}
                                  </h4>
                                  {typeof cert === "object" && (
                                    <div className="mt-1 text-xs text-green-700">
                                      {cert.issuer && (
                                        <span>Issued by: {cert.issuer}</span>
                                      )}
                                      {cert.date && (
                                        <span className="ml-2">
                                          • {cert.date}
                                        </span>
                                      )}
                                      {cert.expiry && (
                                        <span className="ml-2">
                                          • Expires: {cert.expiry}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                  </div>

                  {/* Projects */}
                  {candidate.projects &&
                    Array.isArray(candidate.projects) &&
                    candidate.projects.length > 0 && (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                          Projects
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {candidate.projects.map(
                            (project: any, index: number) => (
                              <div
                                key={index}
                                className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                              >
                                <h4 className="text-sm font-medium text-gray-900 mb-2">
                                  {typeof project === "string"
                                    ? project
                                    : project.name || project.title}
                                </h4>
                                {typeof project === "object" && (
                                  <>
                                    {project.description && (
                                      <p className="text-sm text-gray-700 mb-2">
                                        {project.description}
                                      </p>
                                    )}
                                    {project.technologies &&
                                      Array.isArray(project.technologies) && (
                                        <div className="flex flex-wrap gap-1">
                                          {project.technologies.map(
                                            (tech: string, i: number) => (
                                              <span
                                                key={i}
                                                className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-200 text-gray-800"
                                              >
                                                {tech}
                                              </span>
                                            ),
                                          )}
                                        </div>
                                      )}
                                    {project.url && (
                                      <a
                                        href={project.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-indigo-600 hover:text-indigo-500 mt-2 inline-block"
                                      >
                                        View Project →
                                      </a>
                                    )}
                                  </>
                                )}
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}

                  {/* Awards */}
                  {candidate.awards &&
                    Array.isArray(candidate.awards) &&
                    candidate.awards.length > 0 && (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                          Awards & Achievements
                        </h3>
                        <div className="space-y-3">
                          {candidate.awards.map((award: any, index: number) => (
                            <div
                              key={index}
                              className="p-3 bg-yellow-50 rounded-lg border border-yellow-200"
                            >
                              <h4 className="text-sm font-medium text-yellow-900">
                                {typeof award === "string"
                                  ? award
                                  : award.name || award.title}
                              </h4>
                              {typeof award === "object" && (
                                <div className="mt-1 text-xs text-yellow-700">
                                  {award.issuer && (
                                    <span>Awarded by: {award.issuer}</span>
                                  )}
                                  {award.date && (
                                    <span className="ml-2">• {award.date}</span>
                                  )}
                                  {award.description && (
                                    <p className="mt-1 text-sm text-yellow-800">
                                      {award.description}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Enhanced Matching Insights */}
                  {((candidate.suitable_job_titles?.length ?? 0) > 0 ||
                    (candidate.suitable_industries?.length ?? 0) > 0 ||
                    (candidate.transferable_skills?.length ?? 0) > 0 ||
                    (candidate.profile_tags?.length ?? 0) > 0) && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        AI-Powered Career Insights
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {candidate.suitable_job_titles &&
                          (candidate.suitable_job_titles?.length ?? 0) > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-500 mb-3">
                                Suitable Job Titles
                              </h4>
                              <div className="space-y-2">
                                {/* {candidate.suitable_job_titles
                                  .slice(0, 5)
                                  .map((jobTitle, index) => (
                                    <div
                                      key={index}
                                      className="flex items-center justify-between p-2 bg-indigo-50 rounded-lg"
                                    >
                                      <span className="text-sm font-medium text-indigo-900">
                                        {typeof jobTitle === "string"
                                          ? jobTitle
                                          : jobTitle.title}
                                      </span>
                                      {typeof jobTitle === "object" &&
                                        jobTitle.confidence && (
                                          <span className="text-xs text-indigo-700 bg-indigo-100 px-2 py-1 rounded">
                                            {Math.round(
                                              jobTitle.confidence * 100,
                                            )}
                                            %
                                          </span>
                                        )}
                                    </div>
                                  ))} */}
                              </div>
                            </div>
                          )}

                        {candidate.suitable_industries &&
                          candidate.suitable_industries.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-500 mb-3">
                                Suitable Industries
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {/* {candidate.suitable_industries
                                  .slice(0, 8)
                                  .map((industry, index) => (
                                    <span
                                      key={index}
                                      className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800"
                                    >
                                      {industry}
                                    </span>
                                  ))} */}
                              </div>
                            </div>
                          )}

                        {/* Transferable Skills */}
                        {candidate.transferable_skills &&
                          candidate.transferable_skills.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-500 mb-3">
                                Transferable Skills
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {/* {candidate.transferable_skills
                                  .slice(0, 10)
                                  .map((skill, index) => (
                                    <span
                                      key={index}
                                      className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                    >
                                      {skill}
                                    </span>
                                  ))} */}
                              </div>
                            </div>
                          )}

                        {/* Profile Tags */}
                        {candidate.profile_tags &&
                          candidate.profile_tags.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-500 mb-3">
                                Profile Tags
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {/* {candidate.profile_tags
                                  .slice(0, 10)
                                  .map((tag, index) => (
                                    <span
                                      key={index}
                                      className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800"
                                    >
                                      {tag}
                                    </span>
                                  ))} */}
                              </div>
                            </div>
                          )}
                      </div>

                      {/* Career Progression and Experience Indicators */}
                      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                        {candidate.leadership_experience && (
                          <div className="flex items-center p-3 bg-orange-50 rounded-lg">
                            <svg
                              className="w-5 h-5 text-orange-600 mr-2"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span className="text-sm font-medium text-orange-900">
                              Leadership Experience
                            </span>
                          </div>
                        )}

                        {candidate.remote_work_experience && (
                          <div className="flex items-center p-3 bg-cyan-50 rounded-lg">
                            <svg
                              className="w-5 h-5 text-cyan-600 mr-2"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span className="text-sm font-medium text-cyan-900">
                              Remote Work Ready
                            </span>
                          </div>
                        )}

                        {candidate.international_experience && (
                          <div className="flex items-center p-3 bg-emerald-50 rounded-lg">
                            <svg
                              className="w-5 h-5 text-emerald-600 mr-2"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span className="text-sm font-medium text-emerald-900">
                              International Experience
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Career Progression Path */}
                      {candidate.career_progression_path && (
                        <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                          <h4 className="text-sm font-medium text-purple-900 mb-2">
                            Career Progression Insights
                          </h4>
                          <p className="text-sm text-purple-800">
                            {candidate.career_progression_path}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                </div>
              </div>
              {source === "marketplace" && !candidate.contact_unlocked && (
                <div className="absolute inset-0 flex justify-center items-start pt-10 bg-white/80 backdrop-blur-sm rounded-md p-4">
                  <div className="flex flex-col items-center gap-3">
                  <button
                    onClick={() => {
                      setContactUnlockError(null);
                      handleTalentMarketPlaceViewContact(
                        candidate.id,
                        (data) => {
                          const contactInfo = data?.contact_info ?? data;
                          const updated = {
                            ...candidate,
                            ...contactInfo,
                            contact_unlocked: true,
                          };
                          setCandidate(updated);
                          setToast({
                            message:
                              data?.message ||
                              "Contact information unlocked successfully.",
                            type: "success",
                            isVisible: true,
                          });
                          // Store unlocked candidate so CandidateSearch can sync on return
                          sessionStorage.setItem(
                            "marketplace_unlocked_candidate",
                            JSON.stringify(updated),
                          );
                        },
                        (message) => {
                          setContactUnlockError(message);
                          setToast({
                            message,
                            type: "error",
                            isVisible: true,
                          });
                        },
                      );
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md shadow hover:bg-indigo-700 "
                  >
                    View Contact Details
                  </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Experience Tab */}
          {activeTab === "experience" && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                Work Experience
              </h3>
              {experiences.length > 0 ? (
                <div className="space-y-6">
                  {[...experiences]
                    .sort((a, b) => {
                      // Current job first
                      if (a.is_current) return -1;
                      if (b.is_current) return 1;

                      // Then sort by newest start date
                      return (
                        new Date(b.start_date ?? 0).getTime() -
                        new Date(a.start_date ?? 0).getTime()
                      );
                    })
                    .map((exp, index) => (
                      <div
                        key={index}
                        className="border-l-4 border-indigo-200 pl-6 pb-6"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="text-lg font-medium text-gray-900">
                              {exp.job_title}
                            </h4>
                            <p className="text-md text-indigo-600 font-medium">
                              {exp.company_name}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              {exp.start_date &&
                                formatMonthYear(exp.start_date)}{" "}
                              {"-"}
                              {exp.is_current
                                ? " Present"
                                : exp.end_date &&
                                  ` ${formatMonthYear(exp.end_date)}`}
                              {exp.location && ` • ${exp.location}`}
                              {exp.duration_text &&
                                ` • ${exp.duration_text}`}{" "}
                            </p>
                            {exp.employment_type && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mt-2">
                                {exp.employment_type
                                  .replace("_", " ")
                                  .toUpperCase()}
                              </span>
                            )}
                          </div>
                          {exp.ai_confidence && (
                            <div className="text-right">
                              <p className="text-xs text-gray-500">
                                AI Confidence
                              </p>
                              <p className="text-sm font-medium text-indigo-600">
                                {Math.round(exp.ai_confidence * 100)}%
                              </p>
                            </div>
                          )}
                        </div>
                        {exp.description && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-900 mb-2">
                              Description:
                            </p>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {exp.description}
                            </p>
                          </div>
                        )}
                        {toStringList(exp.responsibilities).length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-900 mb-2">
                              Key Responsibilities:
                            </p>
                            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                              {toStringList(exp.responsibilities).map(
                                (responsibility, i) => (
                                  <li key={i}>{responsibility}</li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}
                        {getExperienceAchievements(exp).length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-900 mb-2">
                              Key Achievements:
                            </p>
                            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                              {getExperienceAchievements(exp).map(
                                (achievement, i) => (
                                  <li key={i}>{achievement}</li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}
                        {exp.technologies_used &&
                          exp.technologies_used.length > 0 && (
                            <div className="mt-3">
                              <p className="text-sm font-medium text-gray-900 mb-2">
                                Technologies Used:
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {/* {exp.technologies_used.map((tech, i) => (
                                  <span
                                    key={i}
                                    className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                  >
                                    {tech}
                                  </span>
                                ))} */}
                              </div>
                            </div>
                          )}
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">💼</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Work Experience
                  </h3>
                  <p className="text-gray-500">
                    No work experience information available for this candidate.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Education Tab */}
          {activeTab === "education" && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                Education
              </h3>
              {candidate.education && candidate.education.length > 0 ? (
                <div className="space-y-6">
                  {candidate.education.map((edu, index) => (
                    <div
                      key={index}
                      className="border-l-4 border-green-200 pl-6 pb-6"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-lg font-medium text-gray-900">
                            {edu.degree_name ||
                              `${edu.degree_type} in ${edu.field_of_study}`}
                          </h4>
                          <p className="text-md text-green-600 font-medium">
                            {edu.institution_name}
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            {edu.graduation_year &&
                              `Graduated: ${edu.graduation_year}`}
                            {edu.start_date &&
                              edu.end_date &&
                              ` • ${new Date(
                                edu.start_date,
                              ).getFullYear()} - ${new Date(
                                edu.end_date,
                              ).getFullYear()}`}
                            {edu.is_current && " • Currently Enrolled"}
                          </p>
                          {edu.gpa && (
                            <p className="text-sm text-gray-600 mt-1">
                              <span className="text-xs text-gray-500">GPA: </span>
                              <span className="font-medium text-green-600">
                                {edu.gpa}
                                {edu.gpa_scale && `/${edu.gpa_scale}`}
                              </span>
                            </p>
                          )}
                          {edu.major && (
                            <p className="text-sm text-gray-600 mt-1">
                              Major: {edu.major}
                            </p>
                          )}
                          {edu.minor && (
                            <p className="text-sm text-gray-600">
                              Minor: {edu.minor}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          {edu.ai_confidence && (
                            <div>
                              <p className="text-xs text-gray-500">
                                AI Confidence
                              </p>
                              <p className="text-sm font-medium text-indigo-600">
                                {Math.round(edu.ai_confidence * 100)}%
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      {edu.honors && edu.honors.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-gray-900 mb-2">
                            Honors & Awards:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {edu.honors.map((honor, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
                              >
                                {honor}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {edu.relevant_coursework &&
                        edu.relevant_coursework.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-900 mb-2">
                              Relevant Coursework:
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {edu.relevant_coursework.map((course, i) => (
                                <span
                                  key={i}
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                >
                                  {course}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">🎓</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Education Information
                  </h3>
                  <p className="text-gray-500">
                    No education information available for this candidate.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Skills Tab */}
          {activeTab === "skills" && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                Technical & Professional Skills
              </h3>
              {candidate.skills && candidate.skills.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {candidate.skills.map((skill: any, index: any) => (
                    <div
                      key={index}
                      className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-indigo-900">
                          {skill.skill_name || "No Skill"}
                        </h4>
                        {skill.is_verified && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                            ✓ Verified
                          </span>
                        )}
                      </div>

                      <div className="space-y-1">
                        {skill.skill_category && (
                          <p className="text-xs text-indigo-600 capitalize">
                            {skill.skill_category}
                          </p>
                        )}
                        <p className="text-xs text-indigo-700">
                          <span className="font-medium">Level:</span>{" "}
                          {skill.proficiency_level || "Not specified"}
                        </p>
                        {skill.years_of_experience != null && (
                          <p className="text-xs text-indigo-700">
                            <span className="font-medium">Experience:</span>{" "}
                            {skill.years_of_experience} yr
                            {skill.years_of_experience !== 1 ? "s" : ""}
                          </p>
                        )}
                        {skill.ai_confidence != null && (
                          <p className="text-xs text-indigo-700">
                            <span className="font-medium">AI Confidence:</span>{" "}
                            {Math.round(skill.ai_confidence * 100)}%
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">⚡</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Skills Listed
                  </h3>
                  <p className="text-gray-500">
                    No technical or professional skills information available
                    for this candidate.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Projects Tab */}
          {activeTab === "projects" && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                Projects & Portfolio
              </h3>
              {candidate.projects && candidate.projects.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {candidate.projects.map((project, index) => (
                    <div
                      key={index}
                      className="p-6 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="text-lg font-semibold text-gray-900">
                          {typeof project === "string" ? project : project.name}
                        </h4>
                        {typeof project === "object" && project.url && (
                          <a
                            href={project.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-emerald-600 hover:text-emerald-500"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                          </a>
                        )}
                      </div>

                      {typeof project === "object" && (
                        <>
                          {project.description && (
                            <p className="text-sm text-gray-700 mb-3">
                              {project.description}
                            </p>
                          )}

                          {project.role && (
                            <p className="text-sm text-gray-600 mb-2">
                              <span className="font-medium">Role:</span>{" "}
                              {project.role}
                            </p>
                          )}

                          {project.technologies &&
                            Array.isArray(project.technologies) &&
                            project.technologies.length > 0 && (
                              <div className="mb-3">
                                <p className="text-sm font-medium text-gray-700 mb-2">
                                  Technologies:
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {project.technologies.map(
                                    (tech, techIndex) => (
                                      <span
                                        key={techIndex}
                                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                      >
                                        {tech}
                                      </span>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}

                          {project.achievements &&
                            Array.isArray(project.achievements) &&
                            project.achievements.length > 0 && (
                              <div>
                                <p className="text-sm font-medium text-gray-700 mb-2">
                                  Key Achievements:
                                </p>
                                <ul className="list-disc list-inside space-y-1">
                                  {project.achievements.map(
                                    (achievement, achIndex) => (
                                      <li
                                        key={achIndex}
                                        className="text-sm text-gray-600"
                                      >
                                        {achievement}
                                      </li>
                                    ),
                                  )}
                                </ul>
                              </div>
                            )}

                          {(project.start_date || project.end_date) && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <p className="text-xs text-gray-500">
                                {project.start_date &&
                                  new Date(
                                    project.start_date,
                                  ).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "short",
                                  })}
                                {project.start_date &&
                                  (project.end_date || project.is_ongoing) &&
                                  " - "}
                                {project.is_ongoing
                                  ? "Ongoing"
                                  : project.end_date
                                  ? new Date(
                                      project.end_date,
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "short",
                                    })
                                  : ""}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">🚀</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Projects Listed
                  </h3>
                  <p className="text-gray-500">
                    No project or portfolio information available for this
                    candidate.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Certifications Tab */}
          {activeTab === "certifications" && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                Professional Certifications
              </h3>
              {candidate.certifications &&
              candidate.certifications.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {candidate.certifications.map((cert, index) => (
                    <div
                      key={index}
                      className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-green-900">
                            {typeof cert === "string" ? cert : cert.name}
                          </h4>
                          {typeof cert === "object" &&
                            cert.issuing_organization && (
                              <p className="text-sm text-green-700 font-medium mt-1">
                                {cert.issuing_organization}
                              </p>
                            )}
                        </div>
                        <svg
                          className="w-6 h-6 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                          />
                        </svg>
                      </div>

                      {typeof cert === "object" && (
                        <div className="space-y-2">
                          {cert.issue_date && (
                            <p className="text-sm text-green-700">
                              <span className="font-medium">Issued:</span>{" "}
                              {new Date(cert.issue_date).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                },
                              )}
                            </p>
                          )}

                          {cert.expiration_date && !cert.does_not_expire && (
                            <p className="text-sm text-green-700">
                              <span className="font-medium">Expires:</span>{" "}
                              {new Date(
                                cert.expiration_date,
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                              })}
                            </p>
                          )}

                          {cert.does_not_expire && (
                            <p className="text-sm text-green-700">
                              <span className="font-medium">Valid:</span> Does
                              not expire
                            </p>
                          )}

                          {cert.credential_id && (
                            <p className="text-sm text-green-600">
                              <span className="font-medium">ID:</span>{" "}
                              {cert.credential_id}
                            </p>
                          )}

                          {cert.credential_url && (
                            <a
                              href={cert.credential_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-sm text-green-600 hover:text-green-500 mt-2"
                            >
                              <svg
                                className="w-4 h-4 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                              Verify Credential
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">🏆</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Certifications Listed
                  </h3>
                  <p className="text-gray-500">
                    No professional certifications available for this candidate.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Additional Tab - Languages & Awards */}
          {activeTab === "additional" && (
            <div className="space-y-8">
              {/* Languages Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  Languages
                </h3>
                {candidate.languages && candidate.languages.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {candidate.languages.map((lang, index) => (
                      <div
                        key={index}
                        className="p-4 bg-blue-50 rounded-lg border border-blue-200"
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-blue-900">
                            {typeof lang === "string" ? lang : (lang.language || lang.name)}
                          </h4>
                          {typeof lang === "object" && lang.is_native && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              Native
                            </span>
                          )}
                        </div>
                        {typeof lang === "object" && lang.proficiency && (
                          <p className="text-sm text-blue-700 mt-1 capitalize">
                            {lang.proficiency} proficiency
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-6 bg-blue-50 rounded-lg border-2 border-dashed border-blue-300">
                    <svg
                      className="mx-auto h-8 w-8 text-blue-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                      />
                    </svg>
                    <p className="mt-2 text-sm text-blue-600">
                      No languages listed
                    </p>
                  </div>
                )}
              </div>

              {/* Awards Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  Awards & Achievements
                </h3>
                {candidate.awards && candidate.awards.length > 0 ? (
                  <div className="space-y-4">
                    {candidate.awards.map((award, index) => (
                      <div
                        key={index}
                        className="p-4 bg-yellow-50 rounded-lg border border-yellow-200"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-lg font-semibold text-yellow-900">
                              {typeof award === "string" ? award : award.title}
                            </h4>
                            {typeof award === "object" &&
                              award.organization && (
                                <p className="text-sm text-yellow-700 font-medium mt-1">
                                  {award.organization}
                                </p>
                              )}
                            {typeof award === "object" && award.date && (
                              <p className="text-sm text-yellow-600 mt-1">
                                {new Date(award.date).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "long",
                                  },
                                )}
                              </p>
                            )}
                            {typeof award === "object" && award.description && (
                              <p className="text-sm text-yellow-800 mt-2">
                                {award.description}
                              </p>
                            )}
                          </div>
                          <svg
                            className="w-6 h-6 text-yellow-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                            />
                          </svg>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-6 bg-yellow-50 rounded-lg border-2 border-dashed border-yellow-300">
                    <svg
                      className="mx-auto h-8 w-8 text-yellow-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                      />
                    </svg>
                    <p className="mt-2 text-sm text-yellow-600">
                      No awards listed
                    </p>
                  </div>
                )}
              </div>

              {/* Other Additional Information */}
              {(candidate.hobbies_interests ||
                candidate.volunteer_experience) && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-6">
                    Additional Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {candidate.hobbies_interests &&
                      candidate.hobbies_interests.length > 0 && (
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <h4 className="text-md font-semibold text-gray-900 mb-3">
                            Interests & Hobbies
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {candidate.hobbies_interests.map((hobby, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-200 text-gray-800"
                              >
                                {hobby}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                    {candidate.volunteer_experience &&
                      candidate.volunteer_experience.length > 0 && (
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <h4 className="text-md font-semibold text-gray-900 mb-3">
                            Volunteer Experience
                          </h4>
                          <div className="space-y-2">
                            {candidate.volunteer_experience.map(
                              (vol, index) => (
                                <div key={index} className="text-sm">
                                  <p className="font-medium text-gray-900">
                                    {typeof vol === "string"
                                      ? vol
                                      : vol.organization}
                                  </p>
                                  {typeof vol === "object" && vol.role && (
                                    <p className="text-gray-600">{vol.role}</p>
                                  )}
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Resumes Tab */}
          {/* {activeTab === "resumes" && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                Resumes & Documents
              </h3>
              {candidate.resumes && candidate.resumes.length > 0 ? (
                <div className="space-y-4">
                  {candidate.resumes.map((resume, index) => (
                    <div
                      key={index}
                      className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <svg
                              className="w-5 h-5 text-gray-400 mr-2"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <h4 className="text-lg font-medium text-gray-900">
                              {resume.original_filename}
                            </h4>
                            {resume.is_primary && (
                              <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                Primary
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">Status</p>
                              <p className="font-medium capitalize">
                                {resume.status.replace("_", " ")}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">File Size</p>
                              <p className="font-medium">
                                {resume.file_size
                                  ? `${(resume.file_size / 1024).toFixed(1)} KB`
                                  : "N/A"}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">Uploaded</p>
                              <p className="font-medium">
                                {new Date(
                                  resume.created_at,
                                ).toLocaleDateString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">AI Provider</p>
                              <p className="font-medium">
                                {resume.ai_provider || "N/A"}
                              </p>
                            </div>
                          </div>
                          {resume.ai_score && (
                            <div className="mt-3">
                              <Tooltip
                                content="Quality of AI resume parsing and data extraction"
                                position="top"
                              >
                                <div className="cursor-help">
                                  <div className="flex items-center mb-1">
                                    <p className="text-sm text-gray-500">
                                      AI Quality Score
                                    </p>
                                    <svg
                                      className="w-4 h-4 ml-1 text-gray-400"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                  <div className="flex items-center mt-1">
                                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                                      <div
                                        className="bg-indigo-600 h-2 rounded-full"
                                        style={{
                                          width: `${resume.ai_score * 100}%`,
                                        }}
                                      ></div>
                                    </div>
                                    <span className="ml-2 text-sm font-medium text-indigo-600">
                                      {Math.round(resume.ai_score * 100)}%
                                    </span>
                                  </div>
                                </div>
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">📄</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Resumes
                  </h3>
                  <p className="text-gray-500">
                    No resume documents available for this candidate.
                  </p>
                </div>
              )}
            </div>
          )} */}
        </div>
      </div>

      {/* Contact Modal */}
      {candidate && (
        <ContactModal
          candidate={candidate}
          isOpen={contactModalOpen}
          onClose={handleCloseContactModal}
          onSend={handleSendMessage}
        />
      )}

      {/* Toast Notification */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={closeToast}
        />
      )}
    </div>
  );
};

export default CandidateProfile;
